/**
 *
 *  /EmailPreferences/Users
 *
 */

import React, { useEffect, useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory, useLocation } from 'react-router-dom'
import { queryVariables } from '@joeyparis/utils'

// Graphql
import useQuery from 'components/UseQuery'
import { useMutation, gql } from '@apollo/client'

// Components
import DropDownMenu from 'components/DropDownMenu'
import EmailPreferences from '..'

// Utils
import { get_timeline } from '../email-timelines'
import { COLORS } from 'global-styles'
import { useCurrentUser, useTooltip } from 'hooks'

export const TypeSelectorContainer = styled.div`
	padding: 15px;
	margin-bottom: 20px;
	color: ${COLORS.rebrand_primary};
	background-color: white;
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: space-between;

	h4 {
		margin: 0;
	}

	span {
		font-size: 1.2rem;
	}

	.title {
	}

	.sub-title {
		display: flex;
		align-items: center;
		border-left: 1px solid ${COLORS.rebrand_primary};
		margin-left: 20px;
		padding-left: 20px;
	}

	.type-selector {
		display: flex;
		align-items: center;
		padding: 0 10px;
		cursor: pointer;
	}
`

const GET_TEMPLATES = gql`
	query contact_email_templates_v2($user_id: ID!, $category: String, $topic: String) {
		contact_email_templates_v2(user_id: $user_id, category: $category, topic: $topic) {
			id
			name
			template_index
			email_template_name
			global_default
			has_html
			can_edit
			default(user_id: $user_id, topic: $topic)
		}
		user(id: $user_id) {
			id
			role
			full_name
			is_artist
			is_ready_made
			communication_preference: communication_preference_by_type(category: $category, topic: $topic) {
				id
				email_template_defaults
				contact_emails
				contact_sms
			}
			main_organization {
				name
				has_changes_on_timeline(category: $category, topic: $topic)
				communication_preference: communication_preference_by_type(category: $category, topic: $topic) {
					id
					email_template_defaults
					contact_emails
					contact_sms
				}
			}
		}
	}
`

const REMOVE_EMAIL_TEMPLATE = gql`
	mutation deleteEmailTemplate($id: ID!) {
		deleteEmailTemplate(input: { id: $id }) {
			email_template {
				id
			}
		}
	}
`

const COPY_EMAIL_TEMPLATE = gql`
	mutation copyEmailTemplate($id: ID!, $user_id: ID, $topic: String) {
		copyEmailTemplate(input: { id: $id, user_id: $user_id, topic: $topic }) {
			email_template {
				id
			}
		}
	}
`

export const options_list = [
	{ value: 'taxes_in_retirement_567', text: 'Taxes in Retirement 567' },
	{ value: 'social_security_567', text: 'Social Security 567' },
	{ value: 'estate_planning_567', text: 'Estate Planning 567' },
	{ value: 'retirement_helper___tax_(canada)_567', text: 'Retirement Helper (Canada) 567' },
	{ value: 'enlighten_567', text: 'Enlighten 567' },
]

export const category_options = {
	event: 'Custom-Crafted',
	event_567: 'Performance-Based',
	appointment: 'Appointments',
}

export const timelines_download_links = {
	event: 'https://leadjig-resources.leadjig.com/Custom-Crafted++Event+Workflow.pdf',
	event_567: 'https://leadjig-resources.leadjig.com/Performance-Based+Event+Workflow.pdf',
}

function UserEmailPreferences({ user_id, category }) {
	const history = useHistory()
	const location = useLocation()
	const current_user = useCurrentUser()
	const [buildTooltip] = useTooltip()
	const { topic, template_index } = queryVariables(location)

	const [selected_topic, setSelectedTopic] = useState('taxes_in_retirement_567')
	const [default_template, setDefaultTemplate] = useState('')

	const redirect_hash = `${category}-timeline`

	const {
		data: { user, contact_email_templates_v2 },
		refetch,
	} = useQuery(GET_TEMPLATES, {
		variables: {
			user_id,
			category: category === 'appointment' ? 'appointment' : 'event',
			topic: category === 'event_567' ? selected_topic : null,
		},
		channels: [user_id],
		default_data: {
			user: {},
		},
		skip: !user_id,
	})

	useEffect(() => {
		refetch()
	}, [selected_topic])

	useEffect(() => {
		if (topic && options_list.some((opt) => opt.value === topic)) {
			setSelectedTopic(topic)
		}
	}, [topic])

	useEffect(() => {
		if (template_index !== null && template_index !== undefined) {
			setDefaultTemplate(template_index)
		}
	}, [template_index])

	const [copyEmailTemplate] = useMutation(COPY_EMAIL_TEMPLATE, {
		onCompleted: ({ copyEmailTemplate: { email_template } }) => {
			refetch()
			history.push(`/email_templates/${email_template?.id}/edit#${redirect_hash}`)
		},
	})

	const [removeEmailTemplate] = useMutation(REMOVE_EMAIL_TEMPLATE, { onCompleted: refetch })

	const timeline_lock_by_user_role = !current_user.is_artist && category === 'event_567'
	const timeline_lock_by_organization = !!user?.main_organization?.has_changes_on_timeline

	const timeline_lock = timeline_lock_by_organization || timeline_lock_by_user_role

	const handleCopyTemplate = (variables) => {
		if (timeline_lock) return

		copyEmailTemplate({
			variables: {
				user_id,
				topic: category === 'event_567' ? selected_topic : null,
				...variables,
			},
		})
	}

	const handleRemoveTemplate = (template_id) => {
		if (timeline_lock) return

		removeEmailTemplate({
			variables: {
				id: template_id,
			},
		})
	}

	return (
		<div>
			<div className="d-flex">
				<TypeSelectorContainer>
					<div className="d-flex">
						<h4 className="title">{category_options[category]} Timeline</h4>

						{category === 'event_567' && (
							<div className="sub-title">
								<span className="">Topic: </span>
								<DropDownMenu
									Button={
										<div className="type-selector">
											<span>
												{options_list.find((opt) => opt.value === selected_topic)?.text}
											</span>

											<FontAwesomeIcon icon="caret-down" className="ml-2" />
										</div>
									}
									button_style={{ textDecoration: 'none' }}
								>
									<ul className="list-unstyled" style={{}}>
										{options_list.map((option) => (
											<li
												key={option.value}
												onClick={() => setSelectedTopic(option.value)}
												role="presentation"
											>
												{option.text}
											</li>
										))}
									</ul>
								</DropDownMenu>
							</div>
						)}
					</div>

					{!(category === 'appointment') && (
						<a href={timelines_download_links[category]} target="_blank" className="btn btn-purple">
							<FontAwesomeIcon icon="download" className="mr-2" />
							Download Timeline
						</a>
					)}
				</TypeSelectorContainer>
			</div>

			{user && user.communication_preference && (
				<EmailPreferences
					email_timeline={get_timeline(category)}
					email_templates={contact_email_templates_v2}
					communication_preference={
						timeline_lock_by_organization
							? user.main_organization.communication_preference
							: user.communication_preference
					}
					copyEmailTemplate={handleCopyTemplate}
					deleteEmailTemplate={handleRemoveTemplate}
					origin={redirect_hash}
					category={category}
					organization={user.main_organization}
					locked_by_user_role={timeline_lock_by_user_role}
					locked_by_organization={timeline_lock_by_organization}
					buildTooltip={buildTooltip}
					default_template_index={default_template}
					for_user
				/>
			)}
		</div>
	)
}

UserEmailPreferences.propTypes = {
	user_id: PropTypes.string,
	category: PropTypes.string,
}

export default UserEmailPreferences
